<template>
  <div class="permission-management">
    <div class="page-head">
      <div>
        <h2>权限分配</h2>
        <span>创建用户并配置左侧菜单权限</span>
      </div>
      <el-button type="primary" size="mini" icon="el-icon-plus" @click="openCreateDialog">创建用户</el-button>
    </div>

    <section class="panel">
      <div class="toolbar">
        <span class="field-label">用户</span>
        <el-select
            v-model="selectedUserId"
            filterable
            placeholder="选择用户"
            size="mini"
            style="width: 260px"
            @change="loadUserMenu">
          <el-option
              v-for="user in users"
              :key="user.id"
              :label="user.username"
              :value="user.id">
            <span>{{ user.username }}</span>
            <span class="option-extra">ID {{ user.id }}</span>
          </el-option>
        </el-select>
        <el-tag v-if="selectedUser" size="mini" :type="selectedUser.accountType === 1 ? 'danger' : 'info'">
          {{ selectedUser.accountType === 1 ? '管理员' : '普通用户' }}
        </el-tag>
        <el-button size="mini" icon="el-icon-refresh" :loading="loading" @click="loadUsers">刷新</el-button>
      </div>

      <div class="permission-area">
        <div v-for="module in editableModules" :key="module" class="module-block">
          <div class="module-title">
            <span>{{ moduleNames[module] || module }}</span>
            <el-button type="text" size="mini" @click="toggleModule(module)">
              {{ isModuleAllSelected(module) ? '取消全选' : '全选' }}
            </el-button>
          </div>
          <el-checkbox-group v-model="menuConfig[module]" class="menu-options">
            <el-checkbox
                v-for="item in allMenuItems[module]"
                :key="item.index"
                :label="item.index">
              {{ item.name }}
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </div>

      <div class="actions">
        <el-button
            type="primary"
            size="mini"
            :loading="saving"
            :disabled="!selectedUserId"
            @click="saveMenu">
          保存权限
        </el-button>
      </div>
    </section>

    <el-dialog
        title="创建用户"
        :visible.sync="createDialogVisible"
        width="720px"
        :close-on-click-modal="false">
      <el-form label-width="90px" size="mini">
        <el-form-item label="用户名">
          <el-input v-model.trim="createForm.username" maxlength="50" placeholder="输入登录用户名"></el-input>
        </el-form-item>
        <el-form-item label="账号类型">
          <el-radio-group v-model="createForm.accountType">
            <el-radio-button :label="0">普通用户</el-radio-button>
            <el-radio-button :label="1">管理员</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="菜单权限">
          <div class="dialog-permissions">
            <div v-for="module in editableModules" :key="'create-' + module" class="module-block compact">
              <div class="module-title">
                <span>{{ moduleNames[module] || module }}</span>
                <el-button type="text" size="mini" @click="toggleCreateModule(module)">
                  {{ isCreateModuleAllSelected(module) ? '取消全选' : '全选' }}
                </el-button>
              </div>
              <el-checkbox-group v-model="createForm.menuConfig[module]" class="menu-options">
                <el-checkbox
                    v-for="item in allMenuItems[module]"
                    :key="'create-' + item.index"
                    :label="item.index">
                  {{ item.name }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="mini" @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="creating" @click="createUser">创建</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { allMenuItems, editableModules, moduleNames } from './menuConfig'

const emptyMenuConfig = () => ({
  juliang: [],
  projectSummary: [],
  tiktok: [],
  tencent: [],
  video: [],
  aigc: [],
  warning: []
})

export default {
  name: 'PermissionManagement',
  data() {
    return {
      users: [],
      selectedUserId: null,
      menuConfig: emptyMenuConfig(),
      loading: false,
      saving: false,
      creating: false,
      createDialogVisible: false,
      createForm: {
        username: '',
        accountType: 0,
        menuConfig: emptyMenuConfig()
      }
    }
  },
  computed: {
    allMenuItems() {
      return allMenuItems
    },
    editableModules() {
      return editableModules
    },
    moduleNames() {
      return moduleNames
    },
    selectedUser() {
      return this.users.find(user => user.id === this.selectedUserId)
    }
  },
  mounted() {
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const response = await this.$axios.get('/permission/users')
        if (response.data.success) {
          this.users = response.data.data || []
          if (!this.selectedUserId && this.users.length > 0) {
            this.selectedUserId = this.users[0].id
            await this.loadUserMenu(this.selectedUserId)
          } else if (this.selectedUserId) {
            await this.loadUserMenu(this.selectedUserId)
          }
        } else {
          this.$message.error(response.data.msg || '获取用户列表失败')
        }
      } catch (error) {
        this.$message.error('获取用户列表失败')
      } finally {
        this.loading = false
      }
    },
    async loadUserMenu(userId) {
      if (!userId) {
        this.menuConfig = emptyMenuConfig()
        return
      }
      try {
        const response = await this.$axios.get(`/permission/user/${userId}/menu`)
        if (response.data.success) {
          this.menuConfig = this.normalizeMenuConfig(response.data.data)
        } else {
          this.menuConfig = emptyMenuConfig()
          this.$message.error(response.data.msg || '获取菜单权限失败')
        }
      } catch (error) {
        this.menuConfig = emptyMenuConfig()
        this.$message.error('获取菜单权限失败')
      }
    },
    async saveMenu() {
      if (!this.selectedUserId) {
        this.$message.warning('请选择用户')
        return
      }
      this.saving = true
      try {
        const response = await this.$axios.post('/permission/user/menu/save', {
          userId: this.selectedUserId,
          menuConfig: this.menuConfig
        })
        if (response.data.success) {
          this.menuConfig = this.normalizeMenuConfig(response.data.data)
          this.$message.success('权限保存成功')
        } else {
          this.$message.error(response.data.msg || '权限保存失败')
        }
      } catch (error) {
        this.$message.error('权限保存失败')
      } finally {
        this.saving = false
      }
    },
    openCreateDialog() {
      this.createForm = {
        username: '',
        accountType: 0,
        menuConfig: emptyMenuConfig()
      }
      this.createDialogVisible = true
    },
    async createUser() {
      if (!this.createForm.username) {
        this.$message.warning('请输入用户名')
        return
      }
      this.creating = true
      try {
        const response = await this.$axios.post('/permission/user/create', {
          username: this.createForm.username,
          accountType: this.createForm.accountType,
          menuConfig: this.createForm.menuConfig
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.$alert(`用户名：${data.username}<br>初始密码：${data.password}`, '用户创建成功', {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '知道了'
          })
          this.createDialogVisible = false
          await this.loadUsers()
          this.selectedUserId = data.userId
          await this.loadUserMenu(data.userId)
        } else {
          this.$message.error(response.data.msg || '创建用户失败')
        }
      } catch (error) {
        this.$message.error('创建用户失败')
      } finally {
        this.creating = false
      }
    },
    normalizeMenuConfig(value) {
      const config = emptyMenuConfig()
      editableModules.forEach(module => {
        config[module] = Array.isArray(value && value[module]) ? value[module] : []
      })
      return config
    },
    toggleModule(module) {
      this.menuConfig[module] = this.isModuleAllSelected(module)
        ? []
        : allMenuItems[module].map(item => item.index)
    },
    toggleCreateModule(module) {
      this.createForm.menuConfig[module] = this.isCreateModuleAllSelected(module)
        ? []
        : allMenuItems[module].map(item => item.index)
    },
    isModuleAllSelected(module) {
      const selected = this.menuConfig[module] || []
      return allMenuItems[module].length > 0 && selected.length === allMenuItems[module].length
    },
    isCreateModuleAllSelected(module) {
      const selected = this.createForm.menuConfig[module] || []
      return allMenuItems[module].length > 0 && selected.length === allMenuItems[module].length
    }
  }
}
</script>

<style scoped>
.permission-management {
  min-height: 100vh;
  padding: 18px;
  background: #f5f7fb;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.page-head h2 {
  margin: 0;
  font-size: 21px;
  font-weight: 600;
}

.page-head span,
.option-extra {
  color: #7a8390;
  font-size: 12px;
}

.panel {
  padding: 14px;
  background: #ffffff;
  border: 1px solid #dfe5ec;
  border-radius: 6px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.field-label {
  color: #38404a;
  font-size: 13px;
}

.permission-area,
.dialog-permissions {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 12px;
}

.module-block {
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fbfcff;
}

.module-block.compact {
  padding: 10px;
}

.module-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2f3540;
}

.menu-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.menu-options .el-checkbox {
  margin-right: 0;
}

.actions {
  margin-top: 14px;
  text-align: right;
}
</style>
